/**
 * TextArea — editorial style: hairline bottom border ONLY (no full box).
 * Autosizes via the modern `field-sizing: content` CSS property where
 * supported (Chromium 123+); a small JS rows-fallback handles other engines.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'> {
  invalid?: boolean;
  helperText?: string;
  errorText?: string;
  /** Minimum visible rows (used by the JS fallback). */
  minRows?: number;
}

const BASE = cn(
  'block w-full bg-transparent resize-none',
  'border-0 border-b border-ink-300',
  'px-0 py-3',
  'font-sans text-base text-ink-900',
  'placeholder:text-ink-400 placeholder:italic placeholder:font-serif',
  'transition-[border-color,border-width] duration-200 ease-out-slow',
  'focus:border-b-2 focus:border-accent focus:outline-none',
  'disabled:opacity-40 disabled:pointer-events-none',
);

function autosize(el: HTMLTextAreaElement, minRows: number): void {
  // JS fallback for engines without `field-sizing: content`.
  el.style.height = 'auto';
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '24');
  const minHeight = lineHeight * minRows;
  el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    {
      invalid,
      helperText,
      errorText,
      className,
      id,
      minRows = 3,
      onChange,
      style,
      ...rest
    },
    forwardedRef,
  ) {
    const reactId = React.useId();
    const textareaId = id ?? `ta_${reactId}`;
    const helperId = helperText ? `${textareaId}-help` : undefined;
    const errorId = errorText ? `${textareaId}-err` : undefined;
    const isInvalid = invalid || Boolean(errorText);
    const describedBy =
      [errorId, helperId].filter(Boolean).join(' ') || undefined;

    const localRef = React.useRef<HTMLTextAreaElement | null>(null);
    React.useImperativeHandle(
      forwardedRef,
      () => localRef.current as HTMLTextAreaElement,
      [],
    );

    React.useLayoutEffect(() => {
      if (localRef.current) autosize(localRef.current, minRows);
    }, [minRows]);

    return (
      <>
        <textarea
          ref={localRef}
          id={textareaId}
          rows={minRows}
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
          style={{ fieldSizing: 'content', ...(style ?? {}) } as React.CSSProperties}
          className={cn(
            BASE,
            isInvalid &&
              'border-error-muted border-b-2 focus:border-error-muted',
            className,
          )}
          onChange={(e) => {
            autosize(e.currentTarget, minRows);
            onChange?.(e);
          }}
          {...rest}
        />
        {errorText ? (
          <p
            id={errorId}
            role="alert"
            className="mt-2 text-xs italic text-error-muted"
          >
            {errorText}
          </p>
        ) : null}
        {helperText && !errorText ? (
          <p id={helperId} className="mt-2 text-xs text-ink-500">
            {helperText}
          </p>
        ) : null}
      </>
    );
  },
);
